// TTS generation for TTSView: single-block Generate (with autoplay of the
// new take), the Generate-All two-pipeline run (GEN + PLAY in parallel,
// vanilla `generateAllBlocks`), and the cancel-on-asset-switch effect.
//
// Playback and pipeline state come from useTakePlayback (passed in as
// `playback`); every write after an `await` goes through the captured asset
// index (Audit M17) and re-resolves the block by its stable id (Audit M7).

import { useEffect, useRef, useState } from 'react';
import type { Asset, Block as BlockT, Take } from '@shared/types';
import { useSessionStore } from '@/stores/session';
import type { useToast } from '@/components/ToastProvider';
import { decodeAndCache, playAudio, type PlayHandle } from '@/lib/audioPlayback';
import { generateTts } from '@/lib/api';
import { describeGenerationError, isGenerationAbort } from '@/lib/generationErrors';
import { autoPositionFavOnTimeline } from '@/lib/autoPositionFav';
import { findBlockIndexById } from '@/lib/blockId';
import { appendTakeToBlocks, patchTakeDuration } from '@/lib/appendTake';
import { requestStopPreview } from '@/lib/pipelineCoordinator';
import { clearPlayback, startPlayback } from '@/stores/playback';
import { resolveBlockVoiceId } from '@/lib/voiceMigration';
import type { useTakePlayback } from './useTakePlayback';

interface UseTtsGenerationOptions {
  blocks: BlockT[];
  activeAsset: (Asset & { _isCustom?: boolean }) | undefined;
  activeAssetVoiceId: string;
  currentAssetIdx: number;
  toolId: string | null;
  toast: ReturnType<typeof useToast>;
  updateAssetBlocks: (assetIdx: number, fn: (prev: BlockT[]) => BlockT[]) => void;
  playback: ReturnType<typeof useTakePlayback>;
}

export function useTtsGeneration({
  blocks,
  activeAsset,
  activeAssetVoiceId,
  currentAssetIdx,
  toolId,
  toast,
  updateAssetBlocks,
  playback,
}: UseTtsGenerationOptions) {
  const {
    setPlaying,
    playerRef,
    playEpochRef,
    duck,
    unduck,
    stopPlayback,
    pipelineMode,
    setPipelineMode,
    setPipelineBlockIdx,
    setPipelineGenIdx,
    pipelineCancelRef,
    pipelinePlayerRef,
    pipelineResolveRef,
    inflightControllersRef,
    cancelPipeline,
    playAndWait,
  } = playback;

  // `generatingIdx` tracks which block (if any) has a TTS request in flight.
  const [generatingIdx, setGeneratingIdx] = useState<number | null>(null);

  // ── Cancel-on-asset-switch ──────────────────────────────────────────
  // When the user navigates to a different asset while generation is
  // mid-flight (single Generate or Generate-All), every background
  // task scoped to the previous asset MUST stop before the new asset's
  // controls are reachable. Without this:
  //   • the in-flight `fetch` keeps running and eventually appends a
  //     take into the OLD asset's block (writes via captured idx),
  //   • `generatingIdx` stays non-null so the new asset's Generate button
  //     reads as disabled,
  //   • `pipelineMode` stays !== 'idle' so Generate-All shows "Cancel"
  //     on the new asset,
  //   • the autoplay handle keeps the previous asset's audio audible.
  // We track the previous index in a ref so this effect only fires on a
  // genuine switch — first mount (`prev === current`) is a no-op so we
  // don't tear down freshly-seeded state.
  const prevAssetIdxRef = useRef(currentAssetIdx);
  useEffect(() => {
    const prev = prevAssetIdxRef.current;
    if (prev === currentAssetIdx) return;
    prevAssetIdxRef.current = currentAssetIdx;
    // 1) Abort every in-flight TTS fetch for the previous asset. Each
    //    handler clears its own controller from the set in its finally
    //    branch — `controller.abort()` causes the awaited fetch to reject
    //    with AbortError, which we already filter out of the failure-toast
    //    path in handleGenerate / handleGenerateAll.
    for (const c of inflightControllersRef.current) {
      try { c.abort(); } catch { /* ignore */ }
    }
    // 2) Cancel any active Generate-All / Play-All pipeline. This stops
    //    the current pipeline audio handle and resolves the awaited
    //    `playAndWait` promise so the loop wakes up and reaches its
    //    finally block (which resets pipelineMode to 'idle').
    cancelPipeline();
    // 3) Stop chip-playback / autoplay so audio doesn't bleed into the
    //    new asset's view.
    stopPlayback();
    // 4) Eagerly reset the local UI state. `generatingIdx`, `pipelineMode`,
    //    `pipelineBlockIdx`, and `pipelineGenIdx` would otherwise stay set
    //    until the aborted promises hit their finally branches — by which
    //    time the user is already on the new asset and sees a disabled /
    //    mislabelled button.
    setGeneratingIdx(null);
    setPipelineMode('idle');
    setPipelineBlockIdx(-1);
    setPipelineGenIdx(-1);
    setPlaying(null);
    // Setters + ref from useTakePlayback are stable identities; listed only
    // because the linter cannot see that across the hook boundary.
  }, [currentAssetIdx, cancelPipeline, stopPlayback, inflightControllersRef, setPipelineMode, setPipelineBlockIdx, setPipelineGenIdx, setPlaying]);

  const handleGenerate = async (idx: number) => {
    if (generatingIdx !== null) return;
    const block = blocks[idx];
    if (!block || block.locked) return;
    const text = block.text.trim();
    if (!text) {
      toast.show('Block is empty, cannot generate.', 2500);
      return;
    }
    const generationVoiceId = resolveBlockVoiceId(block, activeAsset?.voiceIds ?? []);
    if (!toolId || !generationVoiceId) {
      toast.show('Select a voice first.', 2500);
      return;
    }
    // Asset-Index festhalten: alles nach dem `await` schreibt über ihn, nicht
    // über „das aktuelle Asset" (Audit M17 — Asset-Wechsel während Request
    // oder Decode ließ die Dauer bei 0 bzw. traf das falsche Asset).
    const assetIdx = currentAssetIdx;
    const assetVoiceIds = activeAsset?.voiceIds ?? [];

    // Stop any current playback before we start the new audio. This covers
    // chip playback (stopPlayback) AND a running Timeline preview /
    // pipeline — the new take will autoplay below, so nothing else should
    // be audible by then.
    if (pipelineMode !== 'idle') cancelPipeline();
    requestStopPreview();
    stopPlayback();
    setGeneratingIdx(idx);

    // v2 strips the `[tag]` inline markers the same way legacy did; v4 keeps
    // them because the model uses them as audio tags (expression hints).
    const isV4 = block.params.model === 'v4';
    const sendText = isV4
      ? text
      : text.replace(/\[[^\]]*\]/g, '').replace(/\s{2,}/g, ' ').trim();

    // Track this fetch in the in-flight set so an asset switch can abort it
    // mid-flight. The controller is removed in the finally below regardless
    // of success / failure / abort.
    const controller = new AbortController();
    inflightControllersRef.current.add(controller);

    try {
      const buffer = await generateTts({
        toolId,
        text: sendText,
        voiceId: generationVoiceId,
        model: block.params.model,
        v2: block.params.v2,
        v4: block.params.v4,
        assetId: activeAsset?.id,
        signal: controller.signal,
      });

      // Build the Take entry. Duration is filled in once decode resolves.
      // Playback runs through Web Audio (decode cache keyed by take.id) —
      // there is no blob: URL on the Take any more (Audit M9).

      // Snapshot the per-model params so the chip summary stays accurate even
      // if the user changes the panel after generation.
      const paramsSnapshot = isV4
        ? { ...block.params.v4 }
        : { ...block.params.v2 };
      // Take number = new value of block.takeCounter (bumped immediately
      // below). Bake it into the id so the chip label stays stable even when
      // the oldest non-favorite is evicted and indices shift.
      const takeNumber = block.takeCounter + 1;
      const take: Take = {
        id: `take-${takeNumber}-${Date.now()}`,
        buffer,
        duration: 0,
        model: block.params.model,
        paramsSnapshot,
        voiceId: generationVoiceId,
      };

      // Re-resolve the block's CURRENT index by its stable id — a chip drag,
      // a star on any block, typing, or a delete during the generate await
      // can have moved or re-created it, leaving `idx` stale. -1 means the
      // block really was deleted → discard the take and say so (Audit M7:
      // before this, identity-based lookup silently dropped paid takes).
      const liveBlocks = useSessionStore.getState().assetBlocks[assetIdx] ?? [];
      const realIdx = findBlockIndexById(liveBlocks, block.id);
      if (realIdx === -1) {
        toast.show('Block was deleted while generating — take discarded.', 4000);
        return;
      }

      // Push take + bump counter (lib/appendTake.ts) — on the captured asset.
      updateAssetBlocks(assetIdx, (prev) => appendTakeToBlocks(prev, realIdx, take));

      // Resolve the new take's final index *before* wiring playback so that
      // the `onEnded` handler can identity-match precisely. Doing setPlaying
      // twice (once with -1, again with the real index after play()) raced
      // with short/cached autoplay: if `ended` fired before the second
      // setPlaying, the second call re-stamped the playing state and the
      // chip never cleared.
      const freshBlocks = useSessionStore.getState().assetBlocks[assetIdx];
      const finalTakeIdx = Math.max(
        0,
        (freshBlocks?.[realIdx]?.takes.length ?? 1) - 1,
      );
      // Autoplay + chip highlight only make sense while the talent is still
      // looking at this asset.
      const stillOnAsset = useSessionStore.getState().currentAssetIdx === assetIdx;

      if (stillOnAsset) setPlaying({ blockIdx: realIdx, takeIdx: finalTakeIdx });
      // Claim the playback mutex for the autoplaying take. Must happen
      // BEFORE play() so that any racing "start" elsewhere sees us as the
      // active kind and stops cleanly.
      startPlayback('take');
      duck();
      const epoch = ++playEpochRef.current;
      let assignedHandle: PlayHandle | null = null;
      const clearIfThisHandle = () => {
        if (playerRef.current === assignedHandle) {
          playerRef.current = null;
          setPlaying(null);
          clearPlayback('take');
        }
        unduck();
      };
      try {
        // Decode once + cache by take id. Routes through voice-bus for the
        // Safari MediaStream tap, but bypassMixer keeps chip plays at unity
        // gain regardless of where the talent has the Voice fader (these
        // are inspection plays — should be audible at full volume even if
        // the mixer is pulled down).
        const handle = await playAudio({
          source: take.buffer,
          cacheKey: take.id,
          routing: 'voice-bus',
          bypassMixer: true,
          onEnded: clearIfThisHandle,
        });
        // Stale guard: a sibling action (chip click, pipeline) may have
        // bumped the epoch while decode was in flight.
        if (playEpochRef.current !== epoch) {
          try { handle.stop(); } catch { /* ignore */ }
        } else {
          assignedHandle = handle;
          playerRef.current = handle;
        }
        // Now that we have the decoded duration, push it into the store +
        // run auto-position if this take is the favorite. Replaces the old
        // `loadedmetadata` HTMLAudio path.
        const d = handle.getDuration();
        if (Number.isFinite(d) && d > 0) {
          // Locate the block by the take's stable id, not the stale idx —
          // another reorder can land between append and this decode callback.
          // Written to the captured asset (M17).
          updateAssetBlocks(assetIdx, (prev) => {
            const r = patchTakeDuration(prev, take.id, d);
            if (r.blockIdx === -1) return prev;
            return r.wasFav
              ? autoPositionFavOnTimeline(r.blockIdx, r.blocks, assetVoiceIds)
              : r.blocks;
          });
        }
      } catch {
        // Autoplay blocked / decode failed — clear the optimistic state
        // so the chip doesn't stay stuck "playing".
        clearIfThisHandle();
      }
    } catch (err) {
      // Abort surfaces as `AbortError` from the underlying fetch — that's the
      // user-initiated asset-switch path, NOT a generation failure. Swallow it
      // silently so the new asset's view doesn't get a misleading error toast.
      // Alles andere in Talent-Sprache statt Roh-JSON (lib/generationErrors).
      if (!isGenerationAbort(err)) {
        toast.show(describeGenerationError(err), 5000);
      }
    } finally {
      inflightControllersRef.current.delete(controller);
      setGeneratingIdx(null);
    }
  };

  /** Generate one take for `blockIdx` — same wire format as handleGenerate
   *  but returns the created Take (or null on failure) instead of touching
   *  the local playback state. Used by the Generate-All pipeline so it can
   *  drive its own playback/highlight logic. */
  const generateTakeForBlock = async (
    blockIdx: number,
  ): Promise<Take | null> => {
    // Asset-Index festhalten (M17) — siehe handleGenerate.
    const assetIdx = currentAssetIdx;
    const assetVoiceIds = activeAsset?.voiceIds ?? [];
    const storeBlocks =
      useSessionStore.getState().assetBlocks[assetIdx] ??
      useSessionStore.getState().blocks;
    const block = storeBlocks[blockIdx];
    if (!block) return null;
    const text = block.text.trim();
    if (!text || block.locked) return null;
    const generationVoiceId = resolveBlockVoiceId(block, assetVoiceIds);
    if (!toolId || !generationVoiceId) return null;

    const isV4 = block.params.model === 'v4';
    const sendText = isV4
      ? text
      : text.replace(/\[[^\]]*\]/g, '').replace(/\s{2,}/g, ' ').trim();

    // Track this pipeline-step fetch in the same in-flight set as single
    // Generate so the asset-switch effect aborts both kinds uniformly. The
    // surrounding handleGenerateAll's try/finally deletes the controller in
    // the catch path below; on success it's deleted just before return.
    const controller = new AbortController();
    inflightControllersRef.current.add(controller);

    let buffer: ArrayBuffer;
    try {
      buffer = await generateTts({
        toolId,
        text: sendText,
        voiceId: generationVoiceId,
        model: block.params.model,
        v2: block.params.v2,
        v4: block.params.v4,
        assetId: activeAsset?.id,
        signal: controller.signal,
      });
    } catch (err) {
      inflightControllersRef.current.delete(controller);
      // Re-throw — the caller (handleGenerateAll) catches and reports.
      // AbortError ends the loop via pipelineCancelRef anyway.
      throw err;
    }
    inflightControllersRef.current.delete(controller);

    const paramsSnapshot = isV4
      ? { ...block.params.v4 }
      : { ...block.params.v2 };
    const takeNumber = block.takeCounter + 1;
    const take: Take = {
      id: `take-${takeNumber}-${Date.now()}`,
      buffer,
      duration: 0,
      model: block.params.model,
      paramsSnapshot,
      voiceId: generationVoiceId,
    };

    // Re-resolve the block's CURRENT index by its stable id — during the
    // generate await a chip drag, star, edit or delete can have moved or
    // re-created it, making blockIdx stale. -1 means the block really was
    // deleted → discard rather than append to the wrong block (which would
    // attach C's audio to A). Audit M7.
    const liveBlocks = useSessionStore.getState().assetBlocks[assetIdx] ?? [];
    const realIdx = findBlockIndexById(liveBlocks, block.id);
    if (realIdx === -1) {
      toast.show(`Block ${blockIdx + 1} was deleted while generating — take discarded.`, 4000);
      return null;
    }

    // Fill duration once decode resolves (same pattern as handleGenerate so
    // the summary chip shows "· 3.2s" after the fact). The decode is cached
    // by take.id so the imminent pipeline play below is instant — no
    // re-decode, no double-fetch like the old HTMLAudio probe. Written to
    // the captured asset (M17).
    void decodeAndCache(buffer.slice(0), take.id).then((decoded) => {
      const d = decoded.duration;
      if (!Number.isFinite(d) || d <= 0) return;
      updateAssetBlocks(assetIdx, (prev) => {
        const r = patchTakeDuration(prev, take.id, d);
        if (r.blockIdx === -1) return prev;
        return r.wasFav
          ? autoPositionFavOnTimeline(r.blockIdx, r.blocks, assetVoiceIds)
          : r.blocks;
      });
    });

    // Push take + bump counter. No auto-favorite, matching Task 4.2 fix.
    updateAssetBlocks(assetIdx, (prev) => appendTakeToBlocks(prev, realIdx, take));

    return take;
  };

  const handleGenerateAll = async () => {
    if (pipelineMode !== 'idle') {
      // Clicking "Generate All" while it's already running cancels it.
      cancelPipeline();
      return;
    }
    if (!toolId || !activeAssetVoiceId) {
      toast.show('Select a voice first.', 2500);
      return;
    }
    // Stop any Timeline-preview that's running — the pipeline takes over
    // the shared voice-gain bus.
    requestStopPreview();
    stopPlayback();
    // Claim the mutex AFTER stopPlayback() so the take's clearPlayback
    // can't undo the slot we're about to take. Must precede the first
    // playAndWait() audio.play() call below.
    startPlayback('pipeline');
    pipelineCancelRef.current = false;
    setPipelineMode('generate-all');

    // Snapshot block count at pipeline start — splices during pipeline are
    // not expected but the loop should be robust if they happen.
    const startBlocks =
      useSessionStore.getState().assetBlocks[currentAssetIdx] ??
      useSessionStore.getState().blocks;
    const total = startBlocks.length;
    // Track GEN cancellation separately from PLAY cancellation. Only a
    // GEN cancellation (HTTP request aborted mid-flight, or cancel before
    // gen even started) is what the "Generate All cancelled" toast is
    // about — interrupting the trailing autoplayback (e.g. user clicks
    // Upload while the just-generated take is still playing) doesn't
    // count as a cancelled generation; the take is on disk.
    let genCancelled = false;
    let generatedAny = false;

    // ── Legacy two-pipeline pattern (vanilla `generateAllBlocks`,
    //    legacy/index.html ~5290): the GEN loop and the PLAY loop run
    //    concurrently. As soon as block N's HTTP request returns, gen
    //    advances to block N+1 — it does NOT wait for block N's autoplay
    //    to finish. Play picks up each block as soon as the corresponding
    //    resolver fires, which means block N+1 is being generated while
    //    block N is audibly playing. That overlap is the whole point —
    //    talent hears block N while we're already fetching N+1.
    //
    //    Per-block result entry: holds the produced Take (or null on
    //    skip / failure) so the play pipeline knows whether to play.
    type GenResult = { take: Take | null; skipped: boolean };
    const genResolvers: Array<((r: GenResult) => void) | null> = new Array(total).fill(null);
    const genPromises: Promise<GenResult>[] = new Array(total);
    for (let i = 0; i < total; i++) {
      genPromises[i] = new Promise<GenResult>((resolve) => {
        genResolvers[i] = resolve;
      });
    }
    const settle = (i: number, r: GenResult): void => {
      const resolver = genResolvers[i];
      if (!resolver) return; // already settled — duplicate cancel etc.
      genResolvers[i] = null;
      resolver(r);
    };

    // PLAY pipeline: walks blocks in order, awaits each gen-resolver,
    // then plays the resulting take to completion. Runs in parallel
    // with GEN so play-N and gen-(N+1) overlap.
    const playPipeline = (async () => {
      for (let i = 0; i < total; i++) {
        if (pipelineCancelRef.current) break;
        const result = await genPromises[i];
        if (pipelineCancelRef.current) break;
        if (result.skipped || !result.take) continue;
        setPipelineBlockIdx(i);
        await playAndWait(result.take);
        if (pipelineCancelRef.current) break;
      }
    })();

    // GEN pipeline: sequentially runs one HTTP request at a time
    // (matches vanilla — ElevenLabs rate limits favor low concurrency
    // on the talent path). Resolves each block's resolver as soon as
    // the take exists in the store, freeing PLAY to start playing it
    // while we move on to the next gen request.
    //
    // Toast timing: the "Generate All cancelled" / "Generate All
    // complete" toast fires HERE, the moment GEN exits, NOT after the
    // outer Promise.all([gen, play]). The user thinks of "Generate All"
    // as the work that fetches takes from ElevenLabs; the trailing
    // autoplayback is a convenience preview, not part of the gen unit.
    // Without this split the toast would lag until the last take had
    // finished playing — confusing when the user clicks Upload while
    // the audio still tails out.
    const genPipeline = (async () => {
      for (let i = 0; i < total; i++) {
        if (pipelineCancelRef.current) {
          // Settle remaining resolvers so playPipeline doesn't hang.
          for (let j = i; j < total; j++) {
            settle(j, { take: null, skipped: true });
          }
          if (i < total) genCancelled = true;
          return;
        }
        const current =
          useSessionStore.getState().assetBlocks[currentAssetIdx]?.[i];
        if (!current || current.locked || !current.text.trim()) {
          settle(i, { take: null, skipped: true });
          continue;
        }
        setPipelineGenIdx(i);
        try {
          const take = await generateTakeForBlock(i);
          if (take) generatedAny = true;
          settle(i, { take, skipped: !take });
        } catch (err) {
          const isAbort = isGenerationAbort(err);
          if (!isAbort) {
            toast.show(`Block ${i + 1}: ${describeGenerationError(err)}`, 5000);
          } else {
            genCancelled = true;
          }
          settle(i, { take: null, skipped: true });
          if (isAbort) {
            for (let j = i + 1; j < total; j++) {
              settle(j, { take: null, skipped: true });
            }
            return;
          }
        }
      }
      setPipelineGenIdx(-1);
    })();

    // Fire the gen-result toast as soon as GEN finishes — don't wait for
    // PLAY. PLAY runs in the background; if the user interrupts it (e.g.
    // by clicking Upload) that's not a generation event. Awaiting only
    // genPipeline here keeps the toast tied to its actual cause.
    await genPipeline;
    if (genCancelled) toast.show('Generate All cancelled', 2500);
    else if (generatedAny) toast.show('Generate All complete', 2500);

    try {
      // PLAY pipeline still has to drain (or be cancelled) before we
      // release the pipeline mode + mutex slot. Errors are already
      // trapped inside playPipeline.
      await playPipeline;
    } finally {
      setPipelineMode('idle');
      setPipelineBlockIdx(-1);
      setPipelineGenIdx(-1);
      pipelinePlayerRef.current = null;
      pipelineResolveRef.current = null;
      pipelineCancelRef.current = false;
      // Release the playback mutex slot if it's still ours. If a Timeline
      // play / chip click yanked it mid-pipeline they already overwrote it
      // and clearPlayback's identity check will no-op safely.
      clearPlayback('pipeline');
    }
  };

  return { generatingIdx, handleGenerate, handleGenerateAll };
}
